import React, { useState, useEffect } from 'react';
import {
  Crown, Clock, AlertTriangle, Lock, Edit2, Trash2, Plus, Minus,
  Check, X, Loader2, Search, CreditCard, Infinity
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getApi = () => {
  const token = localStorage.getItem('sad_token');
  return axios.create({ baseURL: API + '/api', headers: { Authorization: `Bearer ${token}` } });
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  trial: 'bg-blue-100 text-blue-700',
  grace: 'bg-amber-100 text-amber-700',
  readonly: 'bg-orange-100 text-orange-700',
  locked: 'bg-red-100 text-red-700',
  expired: 'bg-slate-100 text-slate-500',
};

const SuperAdminSubscriptions = () => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editSub, setEditSub] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    const api = getApi();
    try {
      const res = await api.get('/admin/subscriptions');
      setSubs(res.data.subscriptions);
    } catch { toast.error('Failed to load subscriptions'); }
    finally { setLoading(false); }
  };

  const filtered = subs.filter(s => {
    const q = search.toLowerCase();
    return !q || s.shop_name?.toLowerCase().includes(q) || s.plan?.includes(q);
  });

  const openEdit = (sub) => {
    setEditSub(sub);
    setEditForm({ plan: sub.plan || 'pos', billing_cycle: sub.billing_cycle || 'monthly', add_days: '', remove_days: '', lifetime: sub.lifetime || false });
  };

  const saveEdit = async () => {
    setSaving(true);
    const api = getApi();
    try {
      const payload = { plan: editForm.plan, billing_cycle: editForm.billing_cycle };
      if (editForm.lifetime !== editSub.lifetime) payload.lifetime = editForm.lifetime;
      if (editForm.add_days) payload.add_days = parseInt(editForm.add_days);
      if (editForm.remove_days) payload.remove_days = parseInt(editForm.remove_days);
      await api.put(`/admin/subscriptions/${editSub.id}`, payload);
      toast.success('Subscription updated');
      setEditSub(null);
      fetchData();
    } catch { toast.error('Update failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this subscription?')) return;
    const api = getApi();
    try {
      await api.delete(`/admin/subscriptions/${id}`);
      toast.success('Deleted');
      setSubs(prev => prev.filter(s => s.id !== id));
    } catch { toast.error('Delete failed'); }
  };

  return (
    <SuperAdminLayout>
      <div className="space-y-6" data-testid="sad-subscriptions-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Subscriptions</h1>
            <p className="text-slate-400 text-sm">{subs.length} total subscriptions</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by shop name..."
            className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded text-sm text-white placeholder-slate-500 focus:border-amber-500 focus:outline-none"
            data-testid="sub-search" />
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-slate-800/50 border border-slate-700 rounded p-12 text-center text-slate-400">
            No subscriptions found
          </div>
        ) : (
          <div className="bg-slate-800/50 border border-slate-700 rounded overflow-hidden">
            <table className="w-full text-sm" data-testid="sub-table">
              <thead>
                <tr className="border-b border-slate-700 text-left">
                  <th className="px-4 py-3 text-slate-400 font-medium">Shop</th>
                  <th className="px-4 py-3 text-slate-400 font-medium">Plan</th>
                  <th className="px-4 py-3 text-slate-400 font-medium">Billing</th>
                  <th className="px-4 py-3 text-slate-400 font-medium">Status</th>
                  <th className="px-4 py-3 text-slate-400 font-medium">Expires</th>
                  <th className="px-4 py-3 text-slate-400 font-medium">Days Left</th>
                  <th className="px-4 py-3 text-slate-400 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(sub => {
                  const cs = sub.computed_status || {};
                  return (
                    <tr key={sub.id} className="border-b border-slate-700/50 hover:bg-slate-800/80" data-testid={`sub-row-${sub.id}`}>
                      <td className="px-4 py-3 text-white font-medium">{sub.shop_name}</td>
                      <td className="px-4 py-3 text-amber-400 capitalize">{sub.plan}</td>
                      <td className="px-4 py-3 text-slate-300 capitalize">
                        {sub.lifetime ? <span className="flex items-center gap-1"><Infinity className="w-4 h-4" /> Lifetime</span> : sub.billing_cycle}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[cs.status] || 'bg-slate-700 text-slate-400'}`}>
                          {cs.status || 'unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-xs">
                        {sub.lifetime ? 'Never' :
                         sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() :
                         sub.trial_ends_at ? new Date(sub.trial_ends_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-white font-medium">
                        {cs.days_left !== undefined ? cs.days_left : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => openEdit(sub)} className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-amber-400"
                            data-testid={`sub-edit-${sub.id}`}><Edit2 className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(sub.id)} className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-red-400"
                            data-testid={`sub-delete-${sub.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editSub && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEditSub(null)}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-md bg-[#1e293b] border border-slate-700 rounded-lg p-6"
                onClick={e => e.stopPropagation()} data-testid="sub-edit-modal">
                <h3 className="text-lg font-bold text-white mb-1">Edit Subscription</h3>
                <p className="text-slate-400 text-sm mb-4">{editSub.shop_name}</p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1">Plan</label>
                    <select value={editForm.plan} onChange={e => setEditForm(v => ({ ...v, plan: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none">
                      <option value="invoice">Invoice Creator (€4.99/mo)</option>
                      <option value="pos">Shop POS (€19.99/mo)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1">Billing Cycle</label>
                    <select value={editForm.billing_cycle} onChange={e => setEditForm(v => ({ ...v, billing_cycle: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none">
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                      <option value="trial">Trial</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Add Days</label>
                      <input type="number" min="0" value={editForm.add_days}
                        onChange={e => setEditForm(v => ({ ...v, add_days: e.target.value }))}
                        placeholder="e.g. 30, 365"
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none"
                        data-testid="sub-add-days" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Remove Days</label>
                      <input type="number" min="0" value={editForm.remove_days}
                        onChange={e => setEditForm(v => ({ ...v, remove_days: e.target.value }))}
                        placeholder="e.g. 7"
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none"
                        data-testid="sub-remove-days" />
                    </div>
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={editForm.lifetime}
                      onChange={e => setEditForm(v => ({ ...v, lifetime: e.target.checked }))}
                      className="w-5 h-5 accent-amber-500" data-testid="sub-lifetime" />
                    <div>
                      <span className="text-white text-sm font-medium">Lifetime Access</span>
                      <p className="text-slate-400 text-xs">No expiry — never needs to renew</p>
                    </div>
                  </label>
                </div>

                <div className="flex gap-3 mt-6">
                  <button onClick={() => setEditSub(null)} className="flex-1 px-4 py-2 bg-slate-700 text-white rounded text-sm">Cancel</button>
                  <button onClick={saveEdit} disabled={saving}
                    className="flex-1 px-4 py-2 bg-amber-500 text-white rounded text-sm hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="sub-save-edit">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </SuperAdminLayout>
  );
};

export default SuperAdminSubscriptions;
